import React, { Fragment } from "react";
import {
  AutocompleteArrayInput,
  BooleanInput,
  FormDataConsumer,
  ReferenceArrayInput,
  SelectArrayInput,
  SelectInput,
} from "react-admin";
import CustomDateRangeInput from "../../../components/CustomDateRangeInput";
import {
  includeParentOptionList,
  IncludeSubsidyOptionEnum,
  includeSubsidyOptionList,
  IncludeSubsidySettingOptionEnum,
  includeSubsidySettingOptionList,
  ScheduleTypeEnum,
  scheduleTypeList,
  statusList
} from "../const";
import CustomAcademicYearInput from "../../../components/CustomAcademicYearInput";
import CustomCcaAcademicYearInput from "../../../components/CustomCcaAcademicYearInput";
import Alert from "@material-ui/lab/Alert";
import AcademicTermInput from "../../../components/AcademicTermInput";
import CcaAcademicTermInput from "../../../components/CcaAcademicTermInput";
import { makeStyles } from "@material-ui/core";

interface IExportStudentInput {
  schoolId: string;
  isMasterList: boolean;
  isLoading?: boolean;
}

interface IStudentScheduleTypeInput {
  isDisabled?: boolean;
}

const useStyles = makeStyles({
  studentScheduleTermInput: {
    marginTop: "-17px",
  },
  exportToggle: {
    marginTop: "10px",
    marginBottom: "-25px",
  },
  alertIncludeRevenue: {
    marginTop: "-15px",
    marginBottom: "30px",
  },
  includeParentField: {
    marginTop: "25px",
  }
});

const StudentScheduleTypeInput = ({ isDisabled }: IStudentScheduleTypeInput) => {
  return (
    <SelectInput
      source="scheduleType"
      label="Schedule Type"
      choices={scheduleTypeList}
      disabled={isDisabled}
    />
  );
};

const FilterStudentScheduleByTermInput = ({ isDisabled }: IStudentScheduleTypeInput) => {
  const classes = useStyles();

  return (
    <>
      <BooleanInput
        source="isFilterScheduleByTerm"
        label="Filter Student Schedule by Term"
        defaultValue={false}
        disabled={isDisabled}
      />

      <FormDataConsumer>
        {({ formData }) =>
          formData.isFilterScheduleByTerm && (
            <AcademicTermInput
              className={classes.studentScheduleTermInput}
              multipleSelection
              isDisabled={isDisabled}
            />
          )
        }
      </FormDataConsumer>
    </>
  );
};

const FilterStudentScheduleByCcaTermInput = ({ isDisabled }: IStudentScheduleTypeInput) => {
  const classes = useStyles();

  return (
    <>
      <BooleanInput
        source="isFilterScheduleByCcaTerm"
        label="Filter Student Schedule by CCA Term"
        defaultValue={false}
        disabled={isDisabled}
      />

      <FormDataConsumer>
        {({ formData }) =>
          formData.isFilterScheduleByCcaTerm && (
            <CcaAcademicTermInput
              className={classes.studentScheduleTermInput}
              multipleSelection
              isDisabled={isDisabled} />
          )
        }
      </FormDataConsumer>
    </>
  );
};

const ExportStudentInput = ({ isLoading, schoolId, isMasterList }: IExportStudentInput) => {
  const classes = useStyles();

  return (
    <Fragment>
      <BooleanInput
        className={classes.exportToggle}
        source="isFilterByRegistrationDate"
        label="Filter Students by Registration Date"
        defaultValue={false}
        disabled={isLoading}
      />

      <FormDataConsumer>
        {({ formData }) =>
          formData.isFilterByRegistrationDate && (
            <CustomDateRangeInput
              fromSource="startDate"
              toSource="endDate"
              fromLabel="Start Date"
              toLabel="End Date"
              isDisabled={isLoading}
              gridItemWidth={6}
            />
          )
        }
      </FormDataConsumer>

      <BooleanInput
        className={classes.exportToggle}
        source="isFilterByStatus"
        label="Filter Students by Status"
        defaultValue={false}
        disabled={isLoading}
      />

      <FormDataConsumer>
        {({ formData }) =>
          formData.isFilterByStatus && (
            <SelectInput
              label="Status"
              source="status"
              choices={statusList}
              disabled={isLoading}
            />
          )
        }
      </FormDataConsumer>

      {!isMasterList && (
        <BooleanInput
          className={classes.exportToggle}
          source="isIncludeParent"
          label="Include Parent"
          defaultValue={false}
          disabled={isLoading}
        />
      )}

      <FormDataConsumer>
        {({ formData }) =>
          (formData.isIncludeParent || isMasterList) && (
            <SelectInput
              className={
                isMasterList && !formData.isFilterByStatus
                  ? classes.includeParentField
                  : null
              }
              source="includeParentOption"
              label="Include Parent Options"
              choices={includeParentOptionList}
              disabled={isLoading}
            />
          )
        }
      </FormDataConsumer>

      {!isMasterList && (
        <BooleanInput
          className={classes.exportToggle}
          source="isIncludeStudentSchedule"
          label="Include Student Schedule"
          defaultValue={false}
          disabled={isLoading}
        />
      )}

      <FormDataConsumer>
        {({ formData }) =>
          (formData.isIncludeStudentSchedule || isMasterList) && (
            <StudentScheduleTypeInput isDisabled={isLoading} />
          )
        }
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) =>
          (formData.isIncludeStudentSchedule || isMasterList) &&
          (formData.scheduleType === ScheduleTypeEnum.Regular ||
            formData.scheduleType === ScheduleTypeEnum.Both) && (
            <>
              <CustomAcademicYearInput
                schoolId={schoolId}
                isDisabled={isLoading}
              />

              <FilterStudentScheduleByTermInput isDisabled={isLoading} />
            </>
          )
        }
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) =>
          (formData.isIncludeStudentSchedule || isMasterList) &&
          (formData.scheduleType === ScheduleTypeEnum.Cca ||
            formData.scheduleType === ScheduleTypeEnum.Both) && (
            <>
              <CustomCcaAcademicYearInput
                schoolId={schoolId}
                isDisabled={isLoading}
              />

              <FilterStudentScheduleByCcaTermInput isDisabled={isLoading} />
            </>
          )
        }
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) =>
          formData.isIncludeStudentSchedule && !isMasterList && (
            <SelectInput
              source="isIncludeEstimatedRevenue"
              label="Include Estimated Revenue"
              choices={[
                { id: "yes", name: "Yes" },
                { id: "no", name: "No" },
              ]}
              defaultValue="no"
              disabled={isLoading}
            />
          )
        }
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) =>
          formData.isIncludeStudentSchedule &&
          formData.isIncludeEstimatedRevenue === "yes" &&
          !isMasterList && (
            <Alert
              severity="info"
              className={classes.alertIncludeRevenue}
            >
              The revenue estimation is based on the latest rate chart of the school
            </Alert>
          )
        }
      </FormDataConsumer>

      {!isMasterList && (
        <BooleanInput
          className={classes.exportToggle}
          source="isIncludeActiveRoutePlan"
          label="Include Route Plan"
          defaultValue={false}
          disabled={isLoading}
        />
      )}

      <BooleanInput
        style={{marginTop: "10px", marginBottom: "-25px"}}
        source="isIncludeSubsidy"
        label="Include Subsidy"
        defaultValue={false}
        disabled={isLoading}
      />

      <FormDataConsumer>
        {({ formData }) =>
          formData.isIncludeSubsidy && (
            <SelectInput
              source="includeSubsidyOption"
              label="Include Subsidy Options"
              choices={includeSubsidyOptionList}
              disabled={isLoading}
            />
          )
        }
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) =>
          formData.isIncludeSubsidy &&
          formData.includeSubsidyOption === IncludeSubsidyOptionEnum.SubsidySetting &&
          (
            <SelectInput
              source="includeSubsidySettingOption"
              label="Include Subsidy Setting Options"
              choices={includeSubsidySettingOptionList}
              defaultValue={IncludeSubsidySettingOptionEnum.ActiveSubsidyOnly}
              disabled={isLoading}
            />
          )
        }
      </FormDataConsumer>

      <FormDataConsumer>
        {({ formData }) =>
          formData.isIncludeSubsidy &&
          formData.includeSubsidyOption === IncludeSubsidyOptionEnum.SubsidyData &&
          (
            <>
              <ReferenceArrayInput
                label="Rate Chart"
                source="rateChartIds"
                reference="rateCharts"
                sort={{ field: "invoiceIssuedAt", order: "DESC" }}
                filter={{
                  SchoolId: schoolId ?? 0,
                  StatusIn: ["Issued", "Posted"]
                }}
                filterToQuery={(searchText: any) => {
                  if (searchText !== "") return { search: searchText };
                }}
                disabled={isLoading}
              >
                <AutocompleteArrayInput />
              </ReferenceArrayInput>

              <SelectArrayInput
                label="Subsidy Document Type"
                source="subsidyDocumentType"
                choices={[
                  { id: "Invoice", name: "Invoice" },
                  { id: "CreditNote", name: "Credit Note" },
                ]}
                options={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        minWidth: '910px',
                        width: 'auto',
                      }
                    }
                  }
                }}
                disabled={isLoading}
              />

              <SelectArrayInput
                label="Subsidy Document Status"
                source="subsidyDocumentStatus"
                choices={[
                  { id: "Draft", name: "Draft" },
                  { id: "Unposted", name: "Unposted" },
                  { id: "Posted", name: "Posted" },
                ]}
                options={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        minWidth: '910px',
                        width: 'auto',
                      }
                    }
                  }
                }}
                disabled={isLoading}
              />
            </>
          )
        }
      </FormDataConsumer>
    </Fragment>
  )
}

export default ExportStudentInput;